(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const smoothBehavior = () => reduceMotion.matches ? 'auto' : 'smooth';

  function normalize(value) {
    return String(value || '').toLocaleLowerCase().replace(/\s+/g, ' ').trim();
  }

  function getFaqEntries(root) {
    return Array.from(root.querySelectorAll('[data-faq-toggle]')).map((button) => {
      const answerId = button.getAttribute('aria-controls');
      const answer = answerId ? document.getElementById(answerId) : null;
      return {
        button,
        answer,
        question: (button.querySelector('.faq-question__text')?.textContent || button.textContent || '').trim(),
        searchable: normalize(`${button.textContent || ''} ${answer?.textContent || ''}`),
      };
    }).filter((entry) => entry.answer);
  }

  function setDisclosure(entry, open, focus = false) {
    const category = entry.button.closest('.faq-category');
    if (open && category) {
      category.querySelectorAll('[data-faq-toggle][aria-expanded="true"]').forEach((button) => {
        if (button === entry.button) return;
        const answer = document.getElementById(button.getAttribute('aria-controls'));
        button.setAttribute('aria-expanded', 'false');
        if (answer) answer.hidden = true;
      });
    }

    entry.button.setAttribute('aria-expanded', String(open));
    entry.answer.hidden = !open;

    if (open && !reduceMotion.matches && entry.answer.animate) {
      entry.answer.animate(
        [
          { opacity: 0, transform: 'translateY(-25px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 500, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      );
    }

    if (focus) entry.button.focus({ preventScroll: true });
  }

  function revealEntry(entry, focus = true) {
    setDisclosure(entry, true, false);
    entry.button.scrollIntoView({ behavior: smoothBehavior(), block: 'center' });
    if (focus) window.setTimeout(() => entry.button.focus({ preventScroll: true }), reduceMotion.matches ? 0 : 350);
  }

  function initDisclosures(root, entries) {
    entries.forEach((entry) => {
      entry.button.addEventListener('click', () => {
        setDisclosure(entry, entry.button.getAttribute('aria-expanded') !== 'true');
      });
    });

    root.querySelectorAll('[data-faq-question]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const question = normalize(link.dataset.faqQuestion);
        const target = entries.find((entry) => normalize(entry.question).startsWith(question));
        if (!target) return;
        event.preventDefault();
        revealEntry(target);
      });
    });
  }

  function setActiveCategory(root, categoryId) {
    root.querySelectorAll('.faq-sidebar__nav-link').forEach((link) => {
      const active = link.dataset.category === categoryId;
      link.classList.toggle('active', active);
      if (active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });

    const select = root.querySelector('#help-category-select');
    if (select && select.value !== categoryId) select.value = categoryId;
  }

  function goToCategory(root, categoryId) {
    const target = document.getElementById(categoryId);
    if (!target) return;
    setActiveCategory(root, categoryId);
    target.scrollIntoView({ behavior: smoothBehavior(), block: 'start' });
  }

  function initCategories(root) {
    root.querySelectorAll('.faq-sidebar__nav-link').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!document.getElementById(link.dataset.category)) return;
        event.preventDefault();
        goToCategory(root, link.dataset.category);
      });
    });

    const select = root.querySelector('#help-category-select');
    select?.addEventListener('change', () => goToCategory(root, select.value));

    const categories = Array.from(root.querySelectorAll('.faq-category'));
    if (!categories.length || !('IntersectionObserver' in window)) return;
    const updateActiveCategory = () => {
      const anchor = window.innerHeight * 0.3;
      const activeCategory = categories.find((category) => category.getBoundingClientRect().bottom > anchor) || categories.at(-1);
      if (activeCategory) setActiveCategory(root, activeCategory.id);
    };
    const observer = new IntersectionObserver(updateActiveCategory, { rootMargin: '-30% 0px -69% 0px', threshold: 0 });
    categories.forEach((category) => observer.observe(category));
    updateActiveCategory();
  }

  function initSearch(root, entries) {
    const input = root.querySelector('#help-center-search-input');
    const list = root.querySelector('#help-center-search-results');
    const clear = root.querySelector('#help-center-search-clear');
    const status = root.querySelector('#help-center-search-status');
    if (!input || !list || !clear || !status) return;
    const form = input.closest('form');
    form?.addEventListener('submit', (event) => event.preventDefault());

    let matches = [];
    let activeIndex = -1;

    const close = ({ clearInput = false } = {}) => {
      list.hidden = true;
      list.replaceChildren();
      matches = [];
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      input.setAttribute('aria-expanded', 'false');
      if (clearInput) input.value = '';
      clear.hidden = input.value.length === 0;
    };

    const selectResult = (index) => {
      const match = matches[index];
      if (!match) return;
      close({ clearInput: true });
      revealEntry(match);
    };

    const updateActive = (nextIndex) => {
      if (!matches.length) return;
      activeIndex = (nextIndex + matches.length) % matches.length;
      const buttons = Array.from(list.querySelectorAll('.help-center-search__option'));
      buttons.forEach((button, index) => button.setAttribute('aria-selected', String(index === activeIndex)));
      const active = buttons[activeIndex];
      if (active) {
        input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    };

    const render = () => {
      const query = normalize(input.value);
      clear.hidden = query.length === 0;
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
      list.replaceChildren();

      if (query.length < 3) {
        matches = [];
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        status.textContent = query.length ? 'Type at least 3 characters to search.' : '';
        return;
      }

      matches = entries.filter((entry) => entry.searchable.includes(query)).slice(0, 8);
      matches.forEach((entry, index) => {
        const item = document.createElement('li');
        item.setAttribute('role', 'presentation');
        const button = document.createElement('button');
        button.type = 'button';
        button.id = `help-center-search-option-${index}`;
        button.className = 'help-center-search__option';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');
        button.textContent = entry.question;
        button.addEventListener('click', () => selectResult(index));
        item.append(button);
        list.append(item);
      });

      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      status.textContent = matches.length === 1 ? '1 help article found.' : `${matches.length} help articles found.`;

      if (!matches.length) {
        const item = document.createElement('li');
        item.className = 'help-center-search__option';
        item.textContent = 'No results found. Try a different keyword.';
        item.setAttribute('aria-disabled', 'true');
        list.append(item);
      }
    };

    input.addEventListener('input', render);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (list.hidden || !matches.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        updateActive(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        updateActive(activeIndex - 1);
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        event.preventDefault();
        selectResult(activeIndex);
      }
    });
    clear.addEventListener('click', () => {
      close({ clearInput: true });
      input.focus();
      status.textContent = 'Search cleared.';
    });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('.help-center-search')) close();
    });
  }

  function init() {
    const pageMarker = document.querySelector('.help-center-page');
    if (!pageMarker) return;
    const root = document;
    const faqRoot = root.querySelector('.help-center-faq');
    if (!faqRoot) return;
    const entries = getFaqEntries(faqRoot);
    initDisclosures(root, entries);
    initCategories(faqRoot);
    initSearch(root, entries);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
